import { useCallback, useMemo, useRef } from 'react';
import * as R from 'remeda';
import { ID, Url } from '@rossum/api-client';
import { CircularProgress, List, Stack } from '@rossum/ui/material';
import { useLazyWorkspaces } from '../../../../../../../../business/workspaces';
import { ListLoader } from './ListLoader';
import { useIntersectionObserver } from './useIntersectionObserver';
import { WorkspaceFolder } from './WorkspaceFolder';

// TODO: Move this into more appropriate folder when finished (added search, polished UX)
const PREFIX = 'QueueSelection';

const classes = {
  root: `${PREFIX}-root`,
};

type QueueSelectionProps = {
  value: Url[];
  onChange: (newValue: Url[]) => void;
  disabled?: boolean;
};

const QueueSelection = ({ value, onChange, disabled }: QueueSelectionProps) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  const infiniteLoaderRef = useRef<HTMLLIElement | null>(null);

  const { data, status, hasNextPage, isFetching, fetchNextPage } =
    useLazyWorkspaces();

  useIntersectionObserver({
    target: infiniteLoaderRef,
    root: listRef,
    enabled: hasNextPage && !isFetching,
    threshold: 0.5,
    rootMargin: '150px',
    onIntersect: () => fetchNextPage(),
  });

  const valuesForWorkspaces = useMemo(() => {
    if (!data) {
      return {};
    }

    return R.pipe(
      data.pages,
      R.map(page => page.results),
      R.flat(10),
      R.map(ws => ({
        id: ws.id,
        value: R.intersection(ws.queues, value),
      })),
      R.indexBy(o => o.id)
    );
  }, [data, value]);

  const handleWorkspaceOnChange = useCallback(
    (workspaceId: ID, newValues: Url[]) => {
      // take current values from other workspaces and join them with newValue from this workspace
      const result = R.pipe(
        valuesForWorkspaces,
        R.values(),
        R.filter(o => o.id !== workspaceId),
        R.map(o => o.value),
        R.flat(),
        R.concat(newValues)
      );

      onChange(result);
    },
    [onChange, valuesForWorkspaces]
  );

  return (
    <Stack className={classes.root} spacing={3} alignItems="center">
      {status === 'loading' && <CircularProgress />}
      {status === 'success' && (
        <List
          sx={{
            width: 550,
            maxHeight: 550,
            overflowY: 'scroll',
            '& ul': { padding: 0 },
          }}
          disablePadding
          ref={listRef}
        >
          {data?.pages.map(page =>
            page.results.map(workspace => (
              <WorkspaceFolder
                key={workspace.id}
                workspace={workspace}
                value={valuesForWorkspaces[workspace.id]?.value ?? []}
                onChange={val => handleWorkspaceOnChange(workspace.id, val)}
                listRef={listRef}
                disabled={disabled}
              />
            ))
          )}
          {hasNextPage && <ListLoader ref={infiniteLoaderRef} />}
        </List>
      )}
    </Stack>
  );
};

export { QueueSelection };
